/*
    Copyright (C) 2023 - Wheel and Brake - Southwest Airlines
*/
import { Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import WnbModal from "../commons/WnbModalAlert";
import WnbModalSuccess from "../commons/WnbModalSuccess";
import WnbModalError from "../commons/WnbModalError";
import FieldValidation from "../commons/FieldValidation";
import { getAll } from "../../../backend/read/GetAll";
import { create } from "../../../backend/create/Create";
import { update } from "../../../backend/update/Update";
import {ACCESS_TOKEN_KEY} from "../../../utilities/Constants";

const EffectivityForm = (props) => {
  const [showAlert, setShowAlert] = useState(false);
  const [showInactiveAlert, setShowInactiveAlert] = useState(false);
  const [id] = useState(props.id);
  const [effectivityStatus, setEffectivityStatus] = useState((id && props.effectivityStatus) || "active");
  const [effectivityValue, setEffectivityValue] = useState(props.effectivityValue || "");
  const saveLabel = "Save";
  const headerLabel = id ? "Edit" : "Create";
  const activePartsCount = (props.activePartsCount && parseInt(props.activePartsCount)) || 0;
  const inactiveAlertMessage = "This effectivity is associated with " + activePartsCount + ((activePartsCount === 1 && " active part.") || " active parts.") + "  Are you sure you want to INACTIVATE this effectivity value?";
  const [validEffectivityValue, setValidEffectivityValue] = useState(true);
  const [effectivityValueValidationMessage, setEffectivityValueValidationMessage] = useState("");
  const [effectivityValueValidationType, setEffectivityValueValidationType] = useState(props.type || "error");
  const [getEffectivityValues, setGetEffectivityValues] = useState([]);
  const [inputDuplicateValue, setInputDuplicateValue] = useState(true);
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [showNotSavedAlert, setShowNotSavedAlert] = useState(false);
  const [mode] = useState(props.mode || "create");
  const navigate = useNavigate();

  useEffect(() => {
    const getNames = () => {
      getAll("EFFECTIVITY").then(r => {
        JSON.parse(r.body.data).Items.forEach(i => {
          setGetEffectivityValues(current => [...current, i.EFFECTIVITY_ID.toUpperCase()]);
        });
      })
    };
    getNames();
  }, []);

  const onChangeStatus = (event) => {
    const newEffectivityStatus = event.target.value;
    setEffectivityStatus(newEffectivityStatus);
    //display warning popup when marking effectivity as inactive which has active parts
    if (id && newEffectivityStatus === "inactive" && activePartsCount > 0) {
      setShowInactiveAlert(true);
    }
  };

  const onChangeEffectivity = (event) => {
    const value = event.target.value;
    const hasInvalidChar = value.match(/[^A-Za-z0-9\s/-]/gi);
    let newEffectivityValue = value.replace(/[^A-Za-z0-9\s/-]/gi, "");
    const attemptExceed = newEffectivityValue.length > 15;
    newEffectivityValue = newEffectivityValue.substring(0, 15);
    setEffectivityValue(newEffectivityValue);
    const duplicateName = getEffectivityValues.includes(event.target.value.trim().toUpperCase());
    setInputDuplicateValue(false);
    if (hasInvalidChar) {
      setValidation(false, "Effectivity value cannot contain special characters. Please create an Effectivity value using letters and numbers only.");
    } else if (attemptExceed) {
      setValidation(false, "Effectivity value is not valid. Please create an Effectivity value that is 15 characters or less.");
    } else if (duplicateName) {
      setValidation(false, "This Effectivity value is already defined.  Please enter a unique Effectivity value.");
      setInputDuplicateValue(true);
    }
  };

  const setValidation = (valid, message, type) => {
    setValidEffectivityValue(valid);
    setEffectivityValueValidationMessage(message || "");
    setEffectivityValueValidationType(type || "error");
  };

  const closeEffValueError = () => {
    setValidation(true);
  };

  const onSave = () => {
    if (mode === "create") {
      let body = {
          "EFFECTIVITY_ID": {
            "S": effectivityValue.toUpperCase()
          },
          "ACTIVE_FLAG": {
            "BOOL": effectivityStatus === "active"
          },
          "ACCESS_TOKEN": {
              "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
          }
      }
      create("EFFECTIVITY", body).then(resp => {
        if (resp.statusCode === 200) {
          setShowSaveAlert(true);
          setSaveMessage("Effectivity '" + effectivityValue.toUpperCase() + "' saved successfully.");
        } else {
          setSaveErrorMessage("Effectivity '" + effectivityValue.toUpperCase() + "' was not created. Please try again.");
          setShowNotSavedAlert(true);
        }
      });
    }
    else {
      let body = {
          "EFFECTIVITY_ID": {
            "S": effectivityValue.toUpperCase()
          },
          "ACTIVE_FLAG": {
            "BOOL": effectivityStatus === "active"
          },
          "ACCESS_TOKEN": {
              "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
          }
      }
      update("EFFECTIVITY", body).then(resp => {
        if (resp.statusCode === 200) {
          setShowSaveAlert(true);
          setSaveMessage("Effectivity '" + effectivityValue.toUpperCase() + "' updated successfully.");
        } else {
          setSaveErrorMessage("Effectivity '" + effectivityValue.toUpperCase() + "' was not updated. Please try again.");
          setShowNotSavedAlert(true);
        }
      });
    }

  };

  const onCancel = () => {
    setShowAlert(true);
  };

  const onClickModalYes = () => {
    setEffectivityStatus((id && props.effectivityStatus) || "active");
    setEffectivityValue(props.effectivityValue || "");
    closeEffValueError();
    setShowAlert(false);
  };

  const onClickModalNo = () => {
    setShowAlert(false);
  };

  const onClickInactiveModalYes = () => {
    setShowInactiveAlert(false);
  };

  const onClickInactiveModalNo = () => {
    setEffectivityStatus("active");
    setShowInactiveAlert(false);
  };

  const onClickNew = () => {
    if (mode === "update") {
      navigate('/create-effectivity');
    }
    else {
      window.location.reload();
    }
  };

  const onClickShowList = () => {
    navigate('/effectivity-list');
  };

  const onClickOkSaveError = () => {
    setShowNotSavedAlert(false);
  };

  const onClickOkSaveSuccess = () => {
    setShowSaveAlert(false);
  };

  return (
    <div className="form-main">
      <Form className="create-form">
        <div className="form-header">
          <div>{headerLabel} Effectivity</div>
          <div className="header-note"><span style={{ color: 'var(--southwest-red)' }}>&#10045;</span> denotes a mandatory field</div>
        </div>
        <Form.Group className="form-status">
          <Form.Check
            name="effStatus"
            type="radio"
            className="wnb-label"
            label="ACTIVE"
            value="active"
            checked={effectivityStatus === "active"}
            onChange={onChangeStatus}
            bsPrefix="effstatus-radio"
          />
          <Form.Check
            name="effStatus"
            type="radio"
            className="wnb-label"
            label="INACTIVE"
            value="inactive"
            checked={effectivityStatus === "inactive"}
            onChange={onChangeStatus}
          />
        </Form.Group>
        <Form.Group className="form-textfield eff-textfield">
          <Form.Label className="wnb-label">Effectivity Value<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
          <div className="field-control">
            <Form.Control
              type="effectivityValue"
              placeholder="Enter Effectivity Value"
              value={effectivityValue}
              className={(!validEffectivityValue && 'invalid-field-' + effectivityValueValidationType) || ''}
              onChange={onChangeEffectivity}
              disabled={!!id}
            />
            <FieldValidation show={!validEffectivityValue} type={effectivityValueValidationType} message={effectivityValueValidationMessage} onClose={closeEffValueError} />
          </div>
        </Form.Group>
        <Form.Group className="action-panel">
          <Button onClick={onSave} disabled={mode === "create" && (!!!effectivityValue || validEffectivityValue === false || inputDuplicateValue === true)}>{saveLabel}</Button>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        </Form.Group>
      </Form>
      <WnbModal
        title="Warning"
        message="Are you sure you wish to cancel?  Clicking YES will reset all data."
        handleCancel={onClickModalNo}
        handleYes={onClickModalYes}
        showAlert={showAlert}
      />
      <WnbModal
        title="Warning"
        message={inactiveAlertMessage}
        handleCancel={onClickInactiveModalNo}
        handleYes={onClickInactiveModalYes}
        showAlert={showInactiveAlert}
      />
      <WnbModalSuccess
        title="Success"
        message={saveMessage}
        handleOk={onClickOkSaveSuccess}
        handleNew={onClickNew}
        handleShowList={onClickShowList}
        showAlert={showSaveAlert}
      />
      <WnbModalError
        title="Error"
        message={saveErrorMessage}
        handleOk={onClickOkSaveError}
        showAlert={showNotSavedAlert}
      />
    </div>
  );
};

export default EffectivityForm;
